import { TopBar } from "@components/TopBar";
import {
  defaultDRFQueryFunctions,
  NavigationConfig,
  NavigationContextProvider,
  useNavigationContext,
} from "@hooks/useNavigation";
import { IconButton } from "@narmi/design_system";
import { serializeQueryParams } from "@utils/api";
import { useLocation } from "react-router-dom";
import { $adminApi, adminApiFetchClient } from "../../../api";
import { statusToLabel } from "../threadUtil";
import { Thread, ThreadSearchParams, ThreadTab } from "../types";
import styles from "./style.module.css";
import { ThreadContentsContainer } from "./ThreadContentsContainer";

export function ThreadDetailsPageV2({ threadId }: { threadId: string }) {
  const { data: thread } = $adminApi.useSuspenseQuery("get", "/v2/admin/threads/{uuid}/", {
    params: {
      path: {
        uuid: threadId,
      },
    },
  });

  const location = useLocation();
  const query = location.state?.query;

  const { data: threadListData = { results: [], count: 0 } } = $adminApi.useQuery(
    "get",
    "/v2/admin/threads/",
    {
      params: { query },
    },
    {
      enabled: !!query,
    }
  );

  return (
    <NavigationContextProvider
      itemId={threadId}
      items={threadListData.results}
      query={query}
      count={threadListData.count}
      config={threadNavigationConfig}
    >
      <TopBar.Compact>
        <TopBar.Breadcrumb to={query ? `/messages/?${serializeQueryParams(query)}` : "/messages"}>
          Messages
        </TopBar.Breadcrumb>
        <TopBar.Breadcrumb>{thread.subject || "Untitled message"}</TopBar.Breadcrumb>
        <TopBar.Spacer />
        <ThreadNavigator state={thread.state} />
      </TopBar.Compact>
      <ThreadContentsContainer thread={thread} />
    </NavigationContextProvider>
  );
}

function ThreadNavigator({ state }: { state: ThreadTab }) {
  const { onPrev, onNext, count, currentPosition } = useNavigationContext<ThreadSearchParams>();

  return (
    <div className={styles.threadNavigator}>
      <div className={styles.threadNavigatorDetails}>
        <div className="fontColor--secondary">{statusToLabel(state)}</div>
        {currentPosition > 0 && (
          <>
            <div className="fontColor--hint">{"\u2022"}</div>
            <div className="fontColor--secondary">
              {currentPosition} of {count}
            </div>
          </>
        )}
      </div>
      <div className={styles.threadNavigatorButtonContainer}>
        <IconButton
          label="Previous message"
          name="arrow-left"
          disabled={!onPrev}
          onClick={onPrev}
        />
        <IconButton label="Next message" name="arrow-right" disabled={!onNext} onClick={onNext} />
      </div>
    </div>
  );
}

const threadNavigationConfig: NavigationConfig<Thread, ThreadSearchParams> = {
  fetchItems: async (query) => {
    const { data } = await adminApiFetchClient.GET("/v2/admin/threads/", {
      params: { query },
    });
    return data || { results: [] };
  },

  getItemId: (thread) => thread.id,

  buildItemUrl: (threadId) => `/messages/${threadId}`,

  ...defaultDRFQueryFunctions,
};
